import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AudienceSource } from './audience-source';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import moment from 'moment-timezone';
import { Injectable } from '@angular/core';
import { CalculatedAudience } from '../../models/meet/calculated-audience.model';

@Injectable({
  providedIn: 'root',
})
export class DigitalMarketingAudienceSource implements AudienceSource {

  calculatedAudiences = new BehaviorSubject<CalculatedAudience[]>([])
  constructor(
    private readonly httpClient: HttpClient
  ) { }

  public search(locationNumber: string, filters: any): Observable<string[]> {
    const requestPayload = this.mapLegacyFiltersToSearch(filters);
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });

    const cfaIDs: Subject<string[]> = new Subject<string[]>();
    this.httpClient.post(environment.digitalMarketingAudienceServiceURL + '/api/audiences/search', requestPayload, { headers: headers })
      .subscribe({
        next: (response) => {
          cfaIDs.next(response as string[]);
          cfaIDs.complete();
        },
        error: (error) => {
          cfaIDs.error(error.error);
        }
      });
    return cfaIDs.asObservable();
  }

  public createAudience(locationNumber: string, filters: any, groupNow: boolean, sendDate: moment.Moment): Observable<string> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    let requestPayload = {
      search: this.mapLegacyFiltersToSearch(filters)
    }
    requestPayload['calculateBy'] = groupNow ? moment().add(15, 'seconds') : sendDate;
    requestPayload['favorited'] = false;
    requestPayload['name'] = locationNumber + '-create-campaign';
    const audienceID: Subject<string> = new Subject<string>();
    this.httpClient.post(environment.digitalMarketingAudienceServiceURL + '/api/audiences', requestPayload, { headers: headers })
      .subscribe({
        next: (response: { id: string }) => {
          audienceID.next(response.id);
          audienceID.complete();
        },
        error: (error) => {
          audienceID.error(error.error);
        }
      });
    return audienceID.asObservable();
  }

  public updateAudience(locationNumber: string, filters: any, groupNow: boolean, id: string, sendDate: moment.Moment): Observable<string> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    let requestPayload = {}
    if (filters && filters.selectedFilters?.length > 0) {
      requestPayload['search']= this.mapLegacyFiltersToSearch(filters)
    }

    requestPayload['calculateBy'] = groupNow ? moment().add(15, 'seconds') : sendDate;

    const audienceUpdated: Subject<string> = new Subject<string>();
    this.httpClient.patch(environment.digitalMarketingAudienceServiceURL + '/api/audiences/'+ id, requestPayload, { headers: headers })
      .subscribe({
        next: () => {
          audienceUpdated.next(id)
          audienceUpdated.complete();
        },
        error: (error) => {
          audienceUpdated.error(error.error);
        }
      });
    return audienceUpdated.asObservable();
  }

  public getAudience(locationNumber: string, audienceID: string): Observable<any> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const selectedFilters: Subject<any> = new Subject<any>();
    this.httpClient.get(environment.digitalMarketingAudienceServiceURL + '/api/audiences/'+audienceID, { headers: headers })
      .subscribe({
        next: (response:DigitalMarketingAudience) => {
          const calculateBy = moment(response.calculateBy).subtract(15, 'seconds');
          selectedFilters.next({ ...this.mapSearchToLegacyFilters(response.search),
            groupNow:  moment(calculateBy).isSameOrBefore(moment()),
            disableCustomerSelection:  moment(calculateBy).isSameOrBefore(moment())
          } )
          selectedFilters.complete();
        },
        error: (error) => {
          selectedFilters.error(error.error)
        }
      });

    return selectedFilters.asObservable();
  }

  public populateCalculatedAudiences(locationNumber: string, ids: string[]): Observable<CalculatedAudience[]> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const idsString = ids.join(',')
    const calculatedAudiences = new Subject<CalculatedAudience[]>()
    this.httpClient.get(environment.digitalMarketingAudienceServiceURL + '/api/audiences/customers?ids='+ idsString, { headers: headers })
      .subscribe({
        next: (response: CalculatedAudience[]) => {
          calculatedAudiences.next(response)
          calculatedAudiences.complete();
        },
        error: (error) => {
          //lack of audience should not fail the whole calendar
          calculatedAudiences.next([])
          calculatedAudiences.complete();
        }
      });

    return calculatedAudiences.asObservable();
  }

  public populateSingleCalculatedAudience(locationNumber: string, id: string): Observable<CalculatedAudience> {
    const headers = new HttpHeaders({
      'Requested-Localities': locationNumber
    });
    const calculatedAudience = new BehaviorSubject<CalculatedAudience>({} as CalculatedAudience)
    this.httpClient.get(environment.digitalMarketingAudienceServiceURL + '/api/audiences/customers?ids='+ id, { headers: headers })
      .subscribe({
        next: (response: CalculatedAudience[]) => {
          calculatedAudience.next(response[0])
          calculatedAudience.complete();
        },
        error: (error) => {
          calculatedAudience.next(undefined)
          calculatedAudience.complete();
        }
      });

    return calculatedAudience.asObservable();
  }

  // NEW TO OLD -------------------------------------------------------

  public mapSearchToLegacyFilters(response: DigitalMarketingAudienceSearch): LegacyFilters {
    const filters: SpotlightFilter[] = [];
    if (response.query) {
      this.parseOperation(response.query, filters);
    }
    if (response.dateRange) {
      filters.push(this.createDateRangeFilter(response.dateRange));
    }
    if (response.numberOfTransactions) {
      filters.push(this.createTransactionCountFilter(response.numberOfTransactions));
    }
    if (response.includeCustomers) {
      filters.push({
        id: 'include-customers',
        parentCategory: 'individual-customers',
        name: 'Include Customers',
        cfaIdList: response.includeCustomers
      });
    }
    if (response.excludeCustomers) {
      filters.push({
        id: 'exclude-customers',
        parentCategory: 'individual-customers',
        name: 'Exclude Customers',
        cfaIdList: response.excludeCustomers
      });
    }
    if (!response || Object.keys(response).length === 0) {
      filters.push({
        id: 'all-spotlight-customers',
        parentCategory: 'all',
        name: 'All Spotlight Customers',
      })
    }
    return {
      selectedFilters: this.mergeLegacyFilters(filters),
      customGroupIds: response.audienceIDs || [],
    };
  }

  private parseOperation(operation: Operation, filters: SpotlightFilter[]) {
    if ('and' in operation && operation.and != null) {
      for (const op of operation.and) {
        this.parseOperation(op, filters);
      }
    } else if ('or' in operation && operation.or != null) {
      for (const op of operation.or) {
        this.parseOperation(op, filters);
      }
    } else if ('filter' in operation) {
      const factory = filterFactories[operation.filter.name as FilterName];
      if (!factory) {
        throw new Error(`Unknown filter name '${operation.filter.name}' provided`);
      }
      filters.push(factory(operation.filter.where));
    }
  }

  private createDateRangeFilter(dateRange: { startDatetime: string; endDatetime: string }): SpotlightFilter {
    return {
      id: 'custom-date-range',
      parentCategory: 'date-range',
      name: 'Custom Date Range',
      startDate: dateRange.startDatetime,
      endDate: dateRange.endDatetime,
    };
  }

  private createTransactionCountFilter(txnCount: { minimum: number; maximum: number }): SpotlightFilter {
    return {
      id: 'specify-order-number',
      parentCategory: 'ordering',
      name: 'Ordered a Specific Number of Times',
      selectedSliderValue: txnCount.minimum,
      selectedSliderHighValue: txnCount.maximum,
    };
  }

  private mergeLegacyFilters(filters: SpotlightFilter[]): SpotlightFilter[] {
    const filterMap = new Map<string, SpotlightFilter>();

    for (const filter of filters) {
      const existingFilter = filterMap.get(filter.id);
      const mergedFilter = existingFilter
        ? this.mergeSpotlightFilters(existingFilter, filter)
        : { ...filter };

      filterMap.set(filter.id, mergedFilter);
    }

    return Array.from(filterMap.values());
  }

  private mergeSpotlightFilters(target: SpotlightFilter, source: SpotlightFilter): SpotlightFilter {
    return {
      ...target,
      ...this.mergeButtonChipSections(target, source),
      ...this.mergeSubOptions(target, source),
    };
  }

  private mergeButtonChipSections(
    target: SpotlightFilter,
    source: SpotlightFilter
  ): Pick<SpotlightFilter, 'selectedButtonChipSections'> {
    if (!source.selectedButtonChipSections) {
      return {};
    }

    const mergedSections = target.selectedButtonChipSections
      ? [...target.selectedButtonChipSections]
      : [];

    for (const sourceSection of source.selectedButtonChipSections) {
      const existingSection = mergedSections.find(section => section.id === sourceSection.id);

      if (existingSection) {
        this.mergeChipsIntoSection(existingSection, sourceSection);
      } else {
        mergedSections.push({ ...sourceSection });
      }
    }

    return { selectedButtonChipSections: mergedSections };
  }

  private mergeChipsIntoSection(
    targetSection: ButtonSelection,
    sourceSection: ButtonSelection
  ): void {
    if (!sourceSection.selectedButtonChips) {
      return;
    }

    if (!targetSection.selectedButtonChips) {
      targetSection.selectedButtonChips = [];
    }

    const uniqueChips = new Set([
      ...targetSection.selectedButtonChips,
      ...sourceSection.selectedButtonChips,
    ]);

    targetSection.selectedButtonChips = Array.from(uniqueChips);
  }

  private mergeSubOptions(
    target: SpotlightFilter,
    source: SpotlightFilter
  ): Pick<SpotlightFilter, 'subOptions'> {
    if (!source.subOptions) {
      return {};
    }

    const mergedOptions = target.subOptions ? [...target.subOptions] : [];

    for (const sourceOption of source.subOptions) {
      const existingOption = mergedOptions.find(opt => opt.id === sourceOption.id);

      if (existingOption) {
        existingOption.selected = existingOption.selected || sourceOption.selected;
      } else {
        mergedOptions.push({ ...sourceOption });
      }
    }

    return { subOptions: mergedOptions };
  }

  // OLD TO NEW -------------------------------------------------------

  public mapLegacyFiltersToSearch(request: LegacyFilters): Partial<DigitalMarketingAudienceSearch> {
    const searchComponents = this.categorizeFilters(request.selectedFilters);
    const query = this.buildQueryOperation(searchComponents.operations);

    return {
      ...(query && { query }),
      ...(searchComponents.dateRange && { dateRange: searchComponents.dateRange }),
      ...(searchComponents.numberOfTransactions && { numberOfTransactions: searchComponents.numberOfTransactions }),
      ...(searchComponents.includeCustomers && { includeCustomers: searchComponents.includeCustomers }),
      ...(searchComponents.excludeCustomers && { excludeCustomers: searchComponents.excludeCustomers }),
      ...(request.customGroupIds?.length && { audienceIDs: request.customGroupIds }),
    };
  }

  private categorizeFilters(filters: SpotlightFilter[]) {
    const searchComponents = {
      operations: [] as Operation[],
      communityHelperOperations: [] as Operation[],
      dateRange: undefined as { startDatetime: string; endDatetime: string } | undefined,
      numberOfTransactions: undefined as { minimum: number; maximum: number } | undefined,
      includeCustomers: undefined as string[] | undefined,
      excludeCustomers: undefined as string[] | undefined,
    };
    for (const filter of filters) {
      if (this.isDateRangeFilter(filter)) {
        searchComponents.dateRange = this.extractDateRange(filter);
      } else if (this.isTransactionCountFilter(filter)) {
        searchComponents.numberOfTransactions = this.extractTransactionCount(filter);
      } else if (this.isIncludeCustomerFilter(filter)) {
        searchComponents.includeCustomers = filter.cfaIdList;
      } else if (this.isExcludeCustomerFilter(filter)) {
        searchComponents.excludeCustomers = filter.cfaIdList;
      } else if (filter.parentCategory === COMMUNITY_HELPERS_FILTER) {
        searchComponents.communityHelperOperations.push(this.mapCommunityHelperFilter(filter.id));
      } else {
        searchComponents.operations.push(...this.mapSpotlightFilterToOperations(filter));
      }
    }

    if (searchComponents.communityHelperOperations.length === 1) {
      searchComponents.operations.push(searchComponents.communityHelperOperations[0]);
    } else if (searchComponents.communityHelperOperations.length > 1) {
      searchComponents.operations.push({ or: searchComponents.communityHelperOperations });
    }

    return searchComponents;
  }

  private extractDateRange(filter: SpotlightFilter): { startDatetime: string; endDatetime: string } {
    const startTime = filter.startDate == null
      ? ''
      : moment.utc(filter.startDate).toISOString().split('.')[0] + 'Z';
    const endTime = filter.endDate == null
      ? ''
      : moment.utc(filter.endDate).toISOString().split('.')[0] + 'Z';

    return {
      startDatetime: startTime,
      endDatetime: endTime,
    };
  }

  private extractTransactionCount(filter: SpotlightFilter): { minimum: number; maximum: number } {
    return {
      minimum: filter.selectedSliderValue ?? 0,
      maximum: filter.selectedSliderHighValue ?? filter.selectedSliderValue ?? 0,
    };
  }

  private buildQueryOperation(operations: Operation[]): Operation | undefined {
    if (operations.length === 1) {
      return operations[0];
    } else if (operations.length > 1) {
      return { and: operations };
    }
    return undefined;
  }

  private isDateRangeFilter(filter: SpotlightFilter): boolean {
    return filter.id === 'custom-date-range' && !!filter.startDate && !!filter.endDate;
  }

  private isTransactionCountFilter(filter: SpotlightFilter): boolean {
    return filter.id === 'specify-order-number' && filter.selectedSliderValue !== undefined;
  }

  private isIncludeCustomerFilter(filter: SpotlightFilter): boolean {
    return filter.id === 'include-customers' && filter.cfaIdList?.length > 0;
  }

  private isExcludeCustomerFilter(filter: SpotlightFilter): boolean {
    return filter.id === 'exclude-customers' && filter.cfaIdList?.length > 0;
  }

  private mapSpotlightFilterToOperations(filter: SpotlightFilter): Operation[] {
    switch (filter.id) {
      case 'placed-individual-order':
        return this.mapIndividualOrderFilter(filter);
      case 'placed-catering-order':
        return this.mapCateringOrderFilter(filter);
      case SPECIFY_DESTINATION_FILTER:
        return this.mapChipSectionsToOperations(
          filter,
          FILTER_MAPPINGS.destinations,
          'haveOrdered',
          'haveNeverOrdered',
          'specify-destination'
        );
      case SPECIFY_DAY_PART_FILTER:
        return this.mapChipSectionsToOperations(
          filter,
          FILTER_MAPPINGS.dayParts,
          'haveOrderedDuring',
          'haveNeverOrderedDuring',
          'specify-day-part'
        );
      case 'membership':
        return [this.mapSubOptionsToOperation(filter, FILTER_MAPPINGS.memberships, 'membership level')];
      default:
        return this.mapByParentCategory(filter);
    }
  }

  private mapIndividualOrderFilter(filter: SpotlightFilter): Operation[] {
    if (filter.selectedButtonToggle === 'mobile') {
      return [{ filter: { name: 'placedIndividualOrderMobile', where: true } }];
    }
    if (filter.selectedButtonToggle === 'scanned') {
      return [{ filter: { name: 'placedIndividualOrderScanned', where: true } }];
    }
    throw new Error(`Invalid button toggle '${filter.selectedButtonToggle}' for placed-individual-order filter`);
  }

  private mapCateringOrderFilter(filter: SpotlightFilter): Operation[] {
    if (filter.selectedButtonToggle === 'have') {
      return [{ filter: { name: 'placedCateringOrder', where: true } }];
    }
    if (filter.selectedButtonToggle === 'haveNot') {
      return [{ filter: { name: 'placedCateringOrder', where: false } }];
    }
    throw new Error(`Invalid button toggle '${filter.selectedButtonToggle}' for placed-catering-order filter`);
  }

  private mapByParentCategory(filter: SpotlightFilter): Operation[] {
    switch (filter.parentCategory) {
      case COMMUNITY_HELPERS_FILTER:
        return [this.mapCommunityHelperFilter(filter.id)];
      case CUSTOMER_ACTIONS_FILTER:
        return this.mapCustomerActionFilter(filter);
      default:
        throw new Error(`Unable to map unknown filter with id '${filter.id}' and category '${filter.parentCategory}'`);
    }
  }

  private mapChipSectionsToOperations(
    filter: SpotlightFilter,
    chipMap: Record<string, FilterName>,
    positiveSection: string,
    negativeSection: string,
    filterType: string
  ): Operation[] {
    const sections: Record<string, string[]> = {};
    for (const section of filter.selectedButtonChipSections ?? []) {
      if (section.selectedButtonChips) {
        sections[section.id] = section.selectedButtonChips;
      }
    }

    const groupedOperations: Operation[] = [];
    for (const [sectionId, chips] of Object.entries(sections)) {
      const filterNames: FilterName[] = chips.map(chip => {
        if (!(chip in chipMap)) {
          throw new Error(`Unknown chip '${chip}' cannot be mapped to a filter name`);
        }
        return chipMap[chip];
      });

      const where: boolean = this.getSectionWhereValue(sectionId, positiveSection, negativeSection, filterType);
      const operations: Operation[] = filterNames.map(name => ({
        filter: { name, where }
      }));

      groupedOperations.push(where ? { or: operations } : { and: operations });

    }

    if (groupedOperations.length === 0) {
      throw new Error(`No selections found for ${filterType} filter`);
    }
    return groupedOperations;
  }

  private getSectionWhereValue(
    sectionId: string,
    positiveSection: string,
    negativeSection: string,
    filterType: string
  ): boolean {
    if (sectionId === positiveSection) return true;
    if (sectionId === negativeSection) return false;
    throw new Error(`Unknown button chip section id '${sectionId}' for ${filterType} filter`);
  }

  private mapSubOptionsToOperation(
    filter: SpotlightFilter,
    optionMap: Record<string, FilterName>,
    errorContext: string
  ): Operation {
    const operations: Operation[] = [];

    for (const option of filter.subOptions ?? []) {
      if (!option.selected) continue;
      if (!(option.id in optionMap)) {
        throw new Error(`Unknown ${errorContext} '${option.id}' cannot be mapped to a filter name`);
      }
      operations.push({ filter: { name: optionMap[option.id], where: true } });

    }

    if (operations.length === 0) {
      throw new Error(`No ${errorContext} selected for filter`);
    }

    return operations.length === 1 ? operations[0] : { or: operations };
  }

  private mapCommunityHelperFilter(helperId: string): Filter {
    if (!(helperId in FILTER_MAPPINGS.communityHelpers)) {
      throw new Error(`Unknown community helper '${helperId}' cannot be mapped to a filter name`);
    }

    return {
      filter: {
        name: FILTER_MAPPINGS.communityHelpers[helperId as keyof typeof FILTER_MAPPINGS.communityHelpers],
        where: true
      }
    };
  }

  private mapCustomerActionFilter(filter: SpotlightFilter): Operation[] {
    switch (filter.id) {
      case 'new-members-from-code-moo':
        return [{ filter: { name: 'newMemberFromCodeMoo', where: true } }];
      case CODE_MOO_REDEMPTIONS_FILTER:
        return [{ filter: { name: 'redeemedCodeMooReward', where: true } }];
      case 'primarily-visit-my-restaurant':
        return this.mapPrimarilyVisitsFilter(filter);
      default:
        return this.mapSimpleCustomerAction(filter);
    }
  }

  private mapPrimarilyVisitsFilter(filter: SpotlightFilter): Operation[] {
    switch (filter.selectedButtonToggle) {
      case 'does':
        return [{ filter: { name: 'primarilyVisitsMyRestaurant', where: true } }];
      case 'doesNot':
        return [{ filter: { name: 'primarilyVisitsMyRestaurant', where: false } }];
      default:
        throw new Error(`Invalid button toggle '${filter.selectedButtonToggle}' for primarily-visit-my-restaurant filter`);
    }
  }

  private mapSimpleCustomerAction(filter: SpotlightFilter): Operation[] {
    if (!(filter.id in FILTER_MAPPINGS.customerActions)) {
      throw new Error(`Unknown customer action filter '${filter.id}' cannot be mapped to a filter name`);
    }

    return [{
      filter: {
        name: FILTER_MAPPINGS.customerActions[filter.id as keyof typeof FILTER_MAPPINGS.customerActions],
        where: true
      }
    }];
  }

}

interface LegacyFilters {
  selectedFilters: SpotlightFilter[];
  customGroupIds?: string[];
}

interface ButtonSelection {
  id: string;
  selectedButtonChips?: string[];
  selected?: boolean;
  name?: string;
  image?: string;
  subtext?: string;
}

interface SpotlightFilter {
  id: string;
  parentCategory: string;
  name?: string;
  selectedSliderHighValue?: number;
  selectedSliderValue?: number;
  selectedButtonToggle?: string;
  selectedButtonChipSections?: ButtonSelection[];
  subOptions?: ButtonSelection[];
  startDate?: string;
  endDate?: string;
  cfaIdList?: string[];
}

type FilterName =
  | 'placedIndividualOrderMobile'
  | 'placedIndividualOrderScanned'
  | 'placedCateringOrder'
  | 'orderedCarryOut'
  | 'orderedDriveThru'
  | 'orderedCurbside'
  | 'orderedDineIn'
  | 'orderedCFADelivery'
  | 'orderedOnDemand'
  | 'orderedBreakfast'
  | 'orderedLunch'
  | 'orderedAfternoon'
  | 'orderedDinner'
  | 'oneMember'
  | 'silverMember'
  | 'redMember'
  | 'signatureMember'
  | 'newMemberThisMonth'
  | 'newMemberLastMonth'
  | 'teacher'
  | 'military'
  | 'nurse'
  | 'firstResponder'
  | 'newMemberFromCodeMoo'
  | 'redeemedCodeMooReward'
  | 'primarilyVisitsMyRestaurant'
  | 'likelyKidsInHousehold'
  | 'hasUpcomingBirthday'
  | 'hasZeroDollarZeroDiscountTxn'
  | 'lapsedBreakfastEligible'
  | 'reduceDropOffRateEligible'
  | 'cfaDeliveryEligible'
  | 'engageMobileOrderEligible';

const ORDERING_FILTER_CATEGORY = 'ordering';
const SPECIFY_DESTINATION_FILTER = 'specify-destination';
const SPECIFY_DAY_PART_FILTER = 'specify-day-part';
const COMMUNITY_HELPERS_FILTER = 'community-helpers';
const CUSTOMER_ACTIONS_FILTER = 'customer-actions';
const CODE_MOO_REDEMPTIONS_FILTER = 'has-redeemed-code-moo-rewards';

const FILTER_MAPPINGS = {
  destinations: {
    'M-CARRYOUT': 'orderedCarryOut',
    'M-DRIVE-THRU': 'orderedDriveThru',
    'CURBSIDE': 'orderedCurbside',
    'M-DINEIN': 'orderedDineIn',
    'CFA DELIVERY': 'orderedCFADelivery',
    'ON DEMAND': 'orderedOnDemand',
  },
  dayParts: {
    'Breakfast': 'orderedBreakfast',
    'Lunch': 'orderedLunch',
    'Afternoon': 'orderedAfternoon',
    'Dinner': 'orderedDinner',
  },
  memberships: {
    'cfa-one': 'oneMember',
    'cfa-silver': 'silverMember',
    'cfa-red': 'redMember',
    'cfa-signature': 'signatureMember',
    'new-member-this-month': 'newMemberThisMonth',
    'new-member-last-month': 'newMemberLastMonth',
  },
  communityHelpers: {
    'teacher': 'teacher',
    'military': 'military',
    'nurse': 'nurse',
    'responder': 'firstResponder',
  },
  customerActions: {
    'likely-kids-in-the-household': 'likelyKidsInHousehold',
    'upcoming-birthday': 'hasUpcomingBirthday',
    'zero-discount-transaction': 'hasZeroDollarZeroDiscountTxn',
  },
} as const satisfies Record<string, Record<string, FilterName>>;

const MEMBERSHIP_SUPOPTIONS = {
  'cfa-one': {
    enabled: true,
    id: 'cfa-one',
    image: 'https://www.cfacdn.com/img/Spotlight/Icons/OneTierIcon.png',
    name: 'Chick-fil-A One Members',
    parentCategory: 'membership',
    selected: true,
    subtext: '0 - 1,000 points',
  },
  'cfa-silver': {
    enabled: true,
    id: 'cfa-silver',
    image: 'https://www.cfacdn.com/img/Spotlight/Icons/SilverTierIcon.png',
    name: 'Chick-fil-A Silver Members',
    parentCategory: 'membership',
    selected: false,
    subtext: '1,001 - 4,000 points',
  },
  'cfa-red': {
    enabled: true,
    id: 'cfa-red',
    image: 'https://www.cfacdn.com/img/Spotlight/Icons/RedTierIcon.png',
    name: 'Chick-fil-A Red Members',
    parentCategory: 'membership',
    selected: false,
    subtext: '4,001 - 10,000 points',
  },
  'cfa-signature': {
    enabled: true,
    id: 'cfa-signature',
    image: 'https://www.cfacdn.com/img/Spotlight/Icons/SignatureTierIcon.png',
    name: 'Chick-fil-A Signature Members',
    parentCategory: 'membership',
    selected: false,
    subtext: '10,001+ points',
  },
  'new-member-this-month': {
    enabled: true,
    id: 'new-member-this-month',
    name: 'New Members This Month',
    parentCategory: 'membership',
    selected: false,
  },
  'new-member-last-month': {
    enabled: true,
    id: 'new-member-last-month',
    name: 'New Members Last Month',
    parentCategory: 'membership',
    selected: false,
  },
};
function createDestinationFilter(destination: string, where: boolean): SpotlightFilter {
  return {
    id: SPECIFY_DESTINATION_FILTER,
    parentCategory: ORDERING_FILTER_CATEGORY,
    name: 'Specify Destination',
    selectedButtonChipSections: [{
      id: where ? 'haveOrdered' : 'haveNeverOrdered',
      selectedButtonChips: [destination]
    }],
  };
}

function createDayPartFilter(dayPart: string, where: boolean): SpotlightFilter {
  return {
    id: SPECIFY_DAY_PART_FILTER,
    parentCategory: ORDERING_FILTER_CATEGORY,
    name: 'Specify Day Part',
    selectedButtonChipSections: [{
      id: where ? 'haveOrderedDuring' : 'haveNeverOrderedDuring',
      selectedButtonChips: [dayPart]
    }],
  };
}

function createMembershipFilter(selectedMembershipId: string): SpotlightFilter {
  const subOptions = Object.keys(FILTER_MAPPINGS.memberships).map(membershipId => {
    return {
      ...MEMBERSHIP_SUPOPTIONS[membershipId],
      selected: selectedMembershipId == membershipId
    }
  });
  return {
    id: 'membership',
    name: 'Specify Membership',
    parentCategory: ORDERING_FILTER_CATEGORY,
    subOptions: subOptions,
  };
}

function createCommunityHelperFilter(helperId: string): SpotlightFilter {
  return {
    id: helperId,
    name: helperId,
    parentCategory: COMMUNITY_HELPERS_FILTER,
  };
}

function createCustomerActionFilter(actionId: string): SpotlightFilter {
  return {
    id: actionId,
    name: actionId,
    parentCategory: CUSTOMER_ACTIONS_FILTER,
  };
}

const filterFactories: Record<FilterName, (where: boolean) => SpotlightFilter> = {
  placedIndividualOrderMobile: () => ({
    id: 'placed-individual-order',
    parentCategory: ORDERING_FILTER_CATEGORY,
    name: 'Placed Individual Order',
    selectedButtonToggle: 'mobile',
  }),
  placedIndividualOrderScanned: () => ({
    id: 'placed-individual-order',
    parentCategory: ORDERING_FILTER_CATEGORY,
    name: 'Placed Individual Order',
    selectedButtonToggle: 'scanned',
  }),
  placedCateringOrder: (where: boolean) => ({
    id: 'placed-catering-order',
    parentCategory: ORDERING_FILTER_CATEGORY,
    name: 'Placed Catering Order',
    selectedButtonToggle: where ? 'have' : 'haveNot',
  }),
  orderedCarryOut: (where: boolean) => createDestinationFilter('M-CARRYOUT', where),
  orderedDriveThru: (where: boolean) => createDestinationFilter('M-DRIVE-THRU', where),
  orderedCurbside: (where: boolean) => createDestinationFilter('CURBSIDE', where),
  orderedDineIn: (where: boolean) => createDestinationFilter('M-DINEIN', where),
  orderedCFADelivery: (where: boolean) => createDestinationFilter('CFA DELIVERY', where),
  orderedOnDemand: (where: boolean) => createDestinationFilter('ON DEMAND', where),
  orderedBreakfast: (where: boolean) => createDayPartFilter('Breakfast', where),
  orderedLunch: (where: boolean) => createDayPartFilter('Lunch', where),
  orderedAfternoon: (where: boolean) => createDayPartFilter('Afternoon', where),
  orderedDinner: (where: boolean) => createDayPartFilter('Dinner', where),
  oneMember: () => createMembershipFilter('cfa-one'),
  silverMember: () => createMembershipFilter('cfa-silver'),
  redMember: () => createMembershipFilter('cfa-red'),
  signatureMember: () => createMembershipFilter('cfa-signature'),
  newMemberThisMonth: () => createMembershipFilter('new-member-this-month'),
  newMemberLastMonth: () => createMembershipFilter('new-member-last-month'),
  teacher: () => createCommunityHelperFilter('teacher'),
  military: () => createCommunityHelperFilter('military'),
  nurse: () => createCommunityHelperFilter('nurse'),
  firstResponder: () => createCommunityHelperFilter('responder'),
  newMemberFromCodeMoo: () => createCustomerActionFilter('new-members-from-code-moo'),
  redeemedCodeMooReward: () => createCustomerActionFilter('has-redeemed-code-moo-rewards'),
  primarilyVisitsMyRestaurant: (where: boolean) => ({
    id: 'primarily-visit-my-restaurant',
    parentCategory: CUSTOMER_ACTIONS_FILTER,
    name:'Primarily Visit My Restaurant',
    selectedButtonToggle: where ? 'does' : 'doesNot',
  }),
  likelyKidsInHousehold: () => createCustomerActionFilter('likely-kids-in-the-household'),
  hasUpcomingBirthday: () => createCustomerActionFilter('upcoming-birthday'),
  hasZeroDollarZeroDiscountTxn: () => createCustomerActionFilter('zero-discount-transaction'),
  lapsedBreakfastEligible: () => ({
    id: 'grow-breakfast',
    parentCategory: 'discover',
    name: 'Lapsed Breakfast',
  }),
  reduceDropOffRateEligible: () => ({
    id: 'churn-flag',
    parentCategory: 'discover',
    name: 'Reduce Drop Off Rate',
  }),
  cfaDeliveryEligible: () => ({
    id: 'operator-led-delivery',
    parentCategory: 'discover',
    name: 'CFA Delivery',

  }),
  engageMobileOrderEligible: () => ({
    id: 'engage-mobile-order',
    parentCategory: 'discover',
    name: 'Engage Mobile Order',
  }),
};

export interface DigitalMarketingAudience {
  id?: string;
  name?: string;
  search: DigitalMarketingAudienceSearch;
  calculateBy: string;
  calculatedAt?: string;
  status?: string;
}

export interface DigitalMarketingAudienceSearch {
  query?: Operation;
  dateRange?: {
    startDatetime: string;
    endDatetime: string;
  };
  numberOfTransactions?: {
    minimum: number;
    maximum: number;
  };
  audienceIDs?: string[];
  includeCustomers?: string[];
  excludeCustomers?: string[];
}

type Operation = Filter | OrOperation | AndOperation;

interface Filter {
  filter: {
    name: string;
    where: boolean;
  }
}
interface OrOperation {
  or: Operation[];
}
interface AndOperation {
  and: Operation[];
}
